import { useEffect, useState, useCallback } from "react";
import { db } from "./firebase";
import {
  collection, getDocs, query, orderBy, limit,
  doc, deleteDoc, updateDoc, startAfter,
} from "firebase/firestore";
import * as XLSX from "xlsx";

// ── Static field groups (DP + Nominee + Other) ─────────────────────────────
const STATIC_FIELD_GROUPS = [
  {
    group: "🗂️ DP Details",
    fields: [
      { key: "fullName",               label: "Full Name" },
      { key: "code",                   label: "Unique Client Code" },
      { key: "dpName",                 label: "DP Name" },
      { key: "depository",             label: "Depository (NSDL/CDSL)" },
      { key: "category",               label: "Category" },
      { key: "representativeAssessee", label: "Representative Assessee" },
      { key: "dpId",                   label: "DP ID + BO Client ID" },
      { key: "pan",                    label: "PAN" },
      { key: "dob",                    label: "DOB/DOI" },
      { key: "nameAsPerPan",           label: "Name as per PAN" },
      { key: "mobile",                 label: "MO (Mobile)" },
      { key: "email",                  label: "Email" },
      { key: "jointWith",              label: "Joint With" },
      { key: "upiId",                  label: "UPI ID" },
      { key: "bank1",                  label: "Bank 1" },
      { key: "bank2",                  label: "Bank 2" },
      { key: "primaryBank",            label: "Primary Bank" },
      { key: "aadhaar",                label: "Aadhaar No" },
      { key: "dpNote",                 label: "Note (DP)" },
    ],
  },
  {
    group: "👥 Nominee Details",
    fields: [
      { key: "nom_nameAsPerPan",  label: "Nominee Name (as per PAN)" },
      { key: "nom_aadhaarNumber", label: "Nominee Aadhaar" },
      { key: "nom_relation",      label: "Relation" },
    ],
  },
  {
    group: "📝 Other Details",
    fields: [
      { key: "reference",    label: "Reference" },
      { key: "sourceForIpo", label: "Source for IPO" },
      { key: "note",         label: "Note" },
    ],
  },
];

const BANK_SUB_FIELDS = [
  { sub: "bankName",      label: "Name" },
  { sub: "branch",        label: "Branch" },
  { sub: "nameAsPerBank", label: "Name as per Bank" },
  { sub: "accNumber",     label: "Account No" },
  { sub: "accountType",   label: "Account Type" },
  { sub: "ifsc",          label: "IFSC" },
  { sub: "micr",          label: "MICR" },
  { sub: "customerId",    label: "Customer ID" },
  { sub: "netBankingId",  label: "Net Banking ID" },
  { sub: "mo",            label: "MO" },
  { sub: "email",         label: "Email" },
  { sub: "atmCardNo",     label: "ATM Card No" },
  { sub: "expiry",        label: "Expiry" },
  { sub: "cvv",           label: "CVV" },
  { sub: "note",          label: "Note" },
];

const RELATION_OPTIONS     = ["Spouse","Father","Mother","Son","Daughter","Brother","Sister","Guardian","Other"];
const DEPOSITORY_OPTIONS   = ["NSDL", "CDSL"];
const CATEGORY_OPTIONS     = ["Individual", "HUF", "Minor", "Other"];
const ACCOUNT_TYPE_OPTIONS = ["Savings", "Current", "NRE", "NRO", "Other"];

const PAGE_SIZE = 10;

function buildFieldGroups(maxBanks) {
  const bankGroups = Array.from({ length: maxBanks }, (_, i) => ({
    group: `🏦 Bank Details (Account #${i + 1})`,
    fields: BANK_SUB_FIELDS.map((sf) => ({
      key:   `bank${i}_${sf.sub}`,
      label: `Bank ${i + 1} ${sf.label}`,
    })),
  }));
  return [
    STATIC_FIELD_GROUPS[0],
    STATIC_FIELD_GROUPS[1],
    ...bankGroups,
    STATIC_FIELD_GROUPS[2],
  ];
}

function resolveField(u, key) {
  if (key.startsWith("nom_")) {
    const sub = key.replace("nom_", "");
    return u.nomineeDetails?.[sub] ?? "-";
  }
  const bankMatch = key.match(/^bank(\d+)_(.+)$/);
  if (bankMatch) {
    const idx = parseInt(bankMatch[1], 10);
    const sub = bankMatch[2];
    return u.bankDetails?.[idx]?.[sub] ?? "";
  }
  return u[key] ?? "";
}

const emptyBank = () => ({
  bankName: "", branch: "", nameAsPerBank: "", accNumber: "",
  accountType: "", ifsc: "", micr: "", customerId: "",
  netBankingId: "", mo: "", email: "", atmCardNo: "",
  expiry: "", cvv: "", note: "",
});

// ── Shared backdrop style ───────────────────────────────────────────────────
const backdropStyle = {
  position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
  width: "100vw", height: "100vh", background: "rgba(0,0,0,0.45)",
  zIndex: 1000, display: "flex", alignItems: "center",
  justifyContent: "center", padding: "20px", boxSizing: "border-box",
};

const modalBoxStyle = {
  background: "#fff", borderRadius: 14,
  boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
  width: "100%", maxWidth: 480, maxHeight: "90vh",
  overflowY: "auto", position: "relative",
};

// ── Icon Buttons ────────────────────────────────────────────────────────────
function EditIconButton({ onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      title="Edit record"
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        width: 32, height: 32, borderRadius: 8,
        border: `1px solid ${hovered ? "#bfdbfe" : "transparent"}`,
        background: hovered ? "#eff6ff" : "transparent",
        color: hovered ? "#2563eb" : "#9ca3af",
        cursor: "pointer", flexShrink: 0, marginLeft: 2, padding: 0,
        transition: "background 0.15s, border-color 0.15s, color 0.15s",
      }}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2.2"
        strokeLinecap="round" strokeLinejoin="round">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
      </svg>
    </button>
  );
}

function DeleteIconButton({ onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      title="Delete record"
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        width: 32, height: 32, borderRadius: 8,
        border: `1px solid ${hovered ? "#fecaca" : "transparent"}`,
        background: hovered ? "#fef2f2" : "transparent",
        color: hovered ? "#dc2626" : "#9ca3af",
        cursor: "pointer", flexShrink: 0, marginLeft: 2, padding: 0,
        transition: "background 0.15s, border-color 0.15s, color 0.15s",
      }}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2.2"
        strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3 6 5 6 21 6" />
        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
        <path d="M10 11v6M14 11v6" />
        <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
      </svg>
    </button>
  );
}

// ── Delete Confirmation Modal ───────────────────────────────────────────────
function DeleteModal({ record, onClose, onConfirm, deleting }) {
  return (
    <div style={backdropStyle} onClick={onClose}>
      <div style={{ ...modalBoxStyle, maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
        <div style={{
          display: "flex", alignItems: "flex-start", justifyContent: "space-between",
          padding: "18px 20px 12px", borderBottom: "1px solid #f3f4f6",
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#dc2626" }}>🗑️ Delete Record</h3>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: "#6b7280" }}>This action cannot be undone.</p>
          </div>
          <button onClick={onClose} disabled={deleting}
            style={{ background: "none", border: "none", fontSize: 18, color: "#9ca3af", cursor: "pointer", lineHeight: 1, padding: "2px 4px", borderRadius: 6 }}>✕</button>
        </div>
        <div style={{ padding: "16px 20px 20px" }}>
          <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: "14px 16px", marginBottom: 20 }}>
            <p style={{ margin: 0, fontSize: 14, color: "#374151" }}>Are you sure you want to permanently delete the record for:</p>
            <p style={{ margin: "8px 0 4px", fontWeight: 700, fontSize: 16, color: "#111827" }}>{record.fullName || "—"}</p>
            {(record.code || record.pan) && (
              <p style={{ margin: 0, fontSize: 13, color: "#6b7280" }}>{[record.code, record.pan].filter(Boolean).join("  ·  ")}</p>
            )}
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button onClick={onClose} disabled={deleting}
              style={{ padding: "9px 18px", background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: "pointer" }}>Cancel</button>
            <button onClick={onConfirm} disabled={deleting}
              style={{ padding: "9px 20px", background: deleting ? "#fca5a5" : "#dc2626", color: "#fff", border: "none", borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: deleting ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: 6, transition: "background 0.2s" }}>
              {deleting ? (<><span style={{ width: 14, height: 14, border: "2px solid #fff", borderTopColor: "transparent", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} />Deleting…</>) : "🗑️ Yes, Delete"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Edit Modal ──────────────────────────────────────────────────────────────
function EditModal({ record, onClose, onSave, saving }) {
  const [form, setForm] = useState({
    fullName: record.fullName || "",
    code: record.code || "",
    dpName: record.dpName || "",
    depository: record.depository || "",
    category: record.category || "",
    representativeAssessee: record.representativeAssessee || "",
    dpId: record.dpId || "",
    pan: record.pan || "",
    dob: record.dob || "",
    nameAsPerPan: record.nameAsPerPan || "",
    mobile: record.mobile || "",
    email: record.email || "",
    jointWith: record.jointWith || "",
    upiId: record.upiId || "",
    bank1: record.bank1 || "",
    bank2: record.bank2 || "",
    primaryBank: record.primaryBank || "",
    aadhaar: record.aadhaar || "",
    dpNote: record.dpNote || "",
    reference: record.reference || "",
    sourceForIpo: record.sourceForIpo || "",
    note: record.note || "",
  });

  const [bankEntries, setBankEntries] = useState(
    record.bankDetails?.length ? record.bankDetails.map((b) => ({ ...emptyBank(), ...b })) : [emptyBank()]
  );

  const [nominee, setNominee] = useState({
    nameAsPerPan: record.nomineeDetails?.nameAsPerPan || "",
    aadhaarNumber: record.nomineeDetails?.aadhaarNumber || "",
    relation: record.nomineeDetails?.relation || "",
  });

  const [errors, setErrors] = useState({});
  const [activeSection, setActiveSection] = useState("dp");

  const sf  = (key, val) => setForm((f) => ({ ...f, [key]: val }));
  const snf = (key, val) => setNominee((n) => ({ ...n, [key]: val }));
  const sbf = (idx, key, val) =>
    setBankEntries((entries) => entries.map((e, i) => (i === idx ? { ...e, [key]: val } : e)));

  const addBank    = () => setBankEntries((e) => [...e, emptyBank()]);
  const removeBank = (idx) => setBankEntries((e) => e.filter((_, i) => i !== idx));

  const validate = () => {
    const e = {};
    if (!form.fullName.trim()) e.fullName = "Required";
    if (!form.code.trim())     e.code     = "Required";
    if (!form.pan.trim()) { e.pan = "Required"; }
    else if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(form.pan)) { e.pan = "Invalid PAN format"; }
    if (!form.mobile.trim()) { e.mobile = "Required"; }
    else if (!/^\d{10}$/.test(form.mobile)) { e.mobile = "10-digit number required"; }
    if (!form.email.trim()) { e.email = "Required"; }
    else if (!/\S+@\S+\.\S+/.test(form.email)) { e.email = "Invalid email"; }
    if (!form.aadhaar.trim()) { e.aadhaar = "Required"; }
    else if (!/^\d{12}$/.test(form.aadhaar)) { e.aadhaar = "12 digits required"; }
    bankEntries.forEach((b, i) => {
      if (!b.bankName.trim())  e[`bank_${i}_bankName`]  = "Required";
      if (!b.accNumber.trim()) e[`bank_${i}_accNumber`] = "Required";
      if (b.ifsc && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(b.ifsc)) e[`bank_${i}_ifsc`] = "Invalid IFSC";
      if (b.micr && !/^\d{9}$/.test(b.micr)) e[`bank_${i}_micr`] = "9 digits required";
    });
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    onSave({ ...form, pan: form.pan.toUpperCase(), bankDetails: bankEntries, nomineeDetails: nominee });
  };

  const ic = (key) => `input${errors[key] ? " error" : ""}`;
  const sc = (key) => `select${errors[key] ? " error" : ""}`;

  const sectionStyle = (id) => ({
    padding: "7px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600,
    cursor: "pointer", border: "none",
    background: activeSection === id ? "#6366f1" : "#f3f4f6",
    color: activeSection === id ? "#fff" : "#374151",
    transition: "all 0.15s",
  });

  return (
    <div style={backdropStyle} onClick={onClose}>
      <div style={{ ...modalBoxStyle, maxWidth: 680 }} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={{
          display: "flex", alignItems: "flex-start", justifyContent: "space-between",
          padding: "18px 20px 12px", borderBottom: "1px solid #f3f4f6",
          position: "sticky", top: 0, background: "#fff", zIndex: 2,
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#111827" }}>✏️ Edit Record</h3>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: "#6b7280" }}>{record.fullName || "—"}</p>
          </div>
          <button onClick={onClose}
            style={{ background: "none", border: "none", fontSize: 18, color: "#9ca3af", cursor: "pointer", lineHeight: 1, padding: "2px 4px", borderRadius: 6 }}>✕</button>
        </div>

        {/* Section tabs */}
        <div style={{ display: "flex", gap: 6, padding: "10px 20px", borderBottom: "1px solid #f3f4f6", background: "#fafafa", flexWrap: "wrap" }}>
          <button style={sectionStyle("dp")}      onClick={() => setActiveSection("dp")}>🗂️ DP Details</button>
          <button style={sectionStyle("nominee")} onClick={() => setActiveSection("nominee")}>👥 Nominee</button>
          <button style={sectionStyle("bank")}    onClick={() => setActiveSection("bank")}>🏦 Banks</button>
          <button style={sectionStyle("other")}   onClick={() => setActiveSection("other")}>📝 Other</button>
        </div>

        {/* Body */}
        <div style={{ padding: "16px 20px" }}>

          {/* ── DP Details ── */}
          {activeSection === "dp" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 16px" }}>
              {[
                ["fullName", "Full Name", "text", false],
                ["code", "Unique Client Code", "text", false],
                ["dpName", "DP Name", "text", false],
                ["pan", "PAN Number", "text", true],
                ["mobile", "Mobile (MO)", "tel", false],
                ["email", "Email", "email", false],
                ["nameAsPerPan", "Name as per PAN", "text", false],
                ["dob", "DOB / DOI", "date", false],
                ["upiId", "UPI ID", "text", false],
                ["aadhaar", "Aadhaar Number", "text", true],
                ["jointWith", "Joint With", "text", false],
                ["bank1", "Bank 1", "text", false],
                ["bank2", "Bank 2", "text", false],
                ["primaryBank", "Primary Bank", "text", false],
                ["representativeAssessee", "Representative Assessee", "text", false],
              ].map(([key, label, type, mono]) => (
                <div className="field" key={key}>
                  <label className="field-label">{label}</label>
                  <input
                    className={`${ic(key)}${mono ? " mono" : ""}`}
                    type={type}
                    value={form[key]}
                    onChange={(e) => sf(key, type === "tel" ? e.target.value.replace(/\D/g, "") : key === "pan" ? e.target.value.toUpperCase() : e.target.value)}
                  />
                  {errors[key] && <span className="error-msg">⚠ {errors[key]}</span>}
                </div>
              ))}

              <div className="field">
                <label className="field-label">Depository</label>
                <select className={sc("depository")} value={form.depository} onChange={(e) => sf("depository", e.target.value)}>
                  <option value="">-- Select --</option>
                  {DEPOSITORY_OPTIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>

              <div className="field">
                <label className="field-label">Category</label>
                <select className={sc("category")} value={form.category} onChange={(e) => sf("category", e.target.value)}>
                  <option value="">-- Select --</option>
                  {CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              <div className="field" style={{ gridColumn: "1 / -1" }}>
                <label className="field-label">DP ID + BO Client ID</label>
                <input className={`${ic("dpId")} mono`} value={form.dpId} onChange={(e) => sf("dpId", e.target.value)} />
                {errors.dpId && <span className="error-msg">⚠ {errors.dpId}</span>}
              </div>

              <div className="field" style={{ gridColumn: "1 / -1" }}>
                <label className="field-label" style={{ color: "#16a34a" }}>Note (DP) <span style={{ fontSize: 12, fontWeight: 400 }}>(Optional)</span></label>
                <textarea className="input" rows={2} value={form.dpNote} onChange={(e) => sf("dpNote", e.target.value)} />
              </div>
            </div>
          )}

          {/* ── Nominee ── */}
          {activeSection === "nominee" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 16px" }}>
              <div className="field">
                <label className="field-label">Name as per PAN</label>
                <input className="input" value={nominee.nameAsPerPan} onChange={(e) => snf("nameAsPerPan", e.target.value)} />
              </div>
              <div className="field">
                <label className="field-label">Aadhaar Number</label>
                <input className="input mono" maxLength={12} value={nominee.aadhaarNumber}
                  onChange={(e) => snf("aadhaarNumber", e.target.value.replace(/\D/g, ""))} />
              </div>
              <div className="field">
                <label className="field-label">Relation</label>
                <select className="select" value={nominee.relation} onChange={(e) => snf("relation", e.target.value)}>
                  <option value="">-- Select --</option>
                  {RELATION_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            </div>
          )}

          {/* ── Banks ── */}
          {activeSection === "bank" && (
            <div>
              {bankEntries.map((bank, idx) => (
                <div key={idx} style={{ marginBottom: 20, border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", background: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
                    <span style={{ fontWeight: 700, fontSize: 13, color: "#374151" }}>🏦 Account #{idx + 1}</span>
                    {bankEntries.length > 1 && (
                      <button onClick={() => removeBank(idx)}
                        style={{ background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca", borderRadius: 6, padding: "3px 10px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                        ✕ Remove
                      </button>
                    )}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 16px", padding: "14px" }}>
                    {[
                      ["bankName", "Bank Name", false],
                      ["branch", "Branch", false],
                      ["nameAsPerBank", "Name as per Bank", false],
                      ["accNumber", "Account Number", true],
                      ["customerId", "Customer ID", true],
                      ["netBankingId", "Net Banking ID", false],
                      ["mo", "Mobile (MO)", false],
                    ].map(([key, label, mono]) => (
                      <div className="field" key={key}>
                        <label className="field-label">{label}</label>
                        <input
                          className={`input${errors[`bank_${idx}_${key}`] ? " error" : ""}${mono ? " mono" : ""}`}
                          value={bank[key]}
                          onChange={(e) => sbf(idx, key, e.target.value)}
                        />
                        {errors[`bank_${idx}_${key}`] && <span className="error-msg">⚠ {errors[`bank_${idx}_${key}`]}</span>}
                      </div>
                    ))}

                    <div className="field">
                      <label className="field-label">Account Type</label>
                      <select className="select" value={bank.accountType} onChange={(e) => sbf(idx, "accountType", e.target.value)}>
                        <option value="">-- Select --</option>
                        {ACCOUNT_TYPE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>

                    <div className="field">
                      <label className="field-label">IFSC Code</label>
                      <input className={`input mono${errors[`bank_${idx}_ifsc`] ? " error" : ""}`}
                        maxLength={11} value={bank.ifsc}
                        onChange={(e) => sbf(idx, "ifsc", e.target.value.toUpperCase())} />
                      {errors[`bank_${idx}_ifsc`] && <span className="error-msg">⚠ {errors[`bank_${idx}_ifsc`]}</span>}
                    </div>

                    <div className="field">
                      <label className="field-label">MICR Code</label>
                      <input className={`input mono${errors[`bank_${idx}_micr`] ? " error" : ""}`}
                        maxLength={9} value={bank.micr}
                        onChange={(e) => sbf(idx, "micr", e.target.value.replace(/\D/g, ""))} />
                      {errors[`bank_${idx}_micr`] && <span className="error-msg">⚠ {errors[`bank_${idx}_micr`]}</span>}
                    </div>

                    {[
                      ["email", "Email (Optional)", "email"],
                      ["atmCardNo", "ATM Card No (Optional)", "text"],
                      ["expiry", "Expiry MM/YY (Optional)", "text"],
                      ["cvv", "CVV (Optional)", "text"],
                    ].map(([key, label, type]) => (
                      <div className="field" key={key} style={{ borderLeft: "3px solid #16a34a", paddingLeft: 10 }}>
                        <label className="field-label" style={{ color: "#16a34a" }}>{label}</label>
                        <input className="input" type={type} value={bank[key]}
                          onChange={(e) => sbf(idx, key, e.target.value)} />
                      </div>
                    ))}

                    <div className="field" style={{ gridColumn: "1 / -1", borderLeft: "3px solid #16a34a", paddingLeft: 10 }}>
                      <label className="field-label" style={{ color: "#16a34a" }}>Note (Optional)</label>
                      <textarea className="input" rows={2} value={bank.note}
                        onChange={(e) => sbf(idx, "note", e.target.value)} />
                    </div>
                  </div>
                </div>
              ))}
              <button className="btn btn-add" onClick={addBank}>+ Add Another Bank Account</button>
            </div>
          )}

          {/* ── Other ── */}
          {activeSection === "other" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 16px" }}>
              <div className="field">
                <label className="field-label">Reference</label>
                <input className="input" value={form.reference} onChange={(e) => sf("reference", e.target.value)} />
              </div>
              <div className="field">
                <label className="field-label">Source for IPO</label>
                <input className="input" value={form.sourceForIpo} onChange={(e) => sf("sourceForIpo", e.target.value)} />
              </div>
              <div className="field" style={{ gridColumn: "1 / -1" }}>
                <label className="field-label">Note</label>
                <textarea className="input" rows={3} value={form.note} onChange={(e) => sf("note", e.target.value)} />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          display: "flex", justifyContent: "flex-end", gap: 10,
          padding: "14px 20px", borderTop: "1px solid #f3f4f6",
          position: "sticky", bottom: 0, background: "#fff",
        }}>
          <button onClick={onClose}
            style={{ padding: "9px 18px", background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: "pointer" }}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving}
            style={{ padding: "9px 20px", background: saving ? "#a5b4fc" : "#6366f1", color: "#fff", border: "none", borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: saving ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: 6 }}>
            {saving ? (
              <><span style={{ width: 14, height: 14, border: "2px solid #fff", borderTopColor: "transparent", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} />Saving…</>
            ) : "💾 Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Export Modal ────────────────────────────────────────────────────────────
const DEFAULT_SELECTED_KEYS = new Set([]);

function ExportModal({ onClose, onExport, total, fieldGroups }) {
  const [selected, setSelected] = useState(new Set(DEFAULT_SELECTED_KEYS));

  useEffect(() => {
    setSelected(new Set(DEFAULT_SELECTED_KEYS));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldGroups.length]);

  const ALL_KEYS = fieldGroups.flatMap((g) => g.fields.map((f) => f.key));

  const labelOf = (key) => {
    for (const g of fieldGroups) {
      const f = g.fields.find((f) => f.key === key);
      if (f) return f.label;
    }
    return key;
  };

  const toggle = (key) =>
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(key) ? n.delete(key) : n.add(key);
      return n;
    });

  const toggleGroup = (fields) => {
    const keys  = fields.map((f) => f.key);
    const allOn = keys.every((k) => selected.has(k));
    setSelected((prev) => {
      const n = new Set(prev);
      keys.forEach((k) => (allOn ? n.delete(k) : n.add(k)));
      return n;
    });
  };

  return (
    <div style={backdropStyle} onClick={onClose}>
      <div style={modalBoxStyle} onClick={(e) => e.stopPropagation()}>
        <div style={{
          display: "flex", alignItems: "flex-start", justifyContent: "space-between",
          padding: "18px 20px 12px", borderBottom: "1px solid #f3f4f6",
          position: "sticky", top: 0, background: "#fff", zIndex: 1,
        }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#111827" }}>Export to Excel</h3>
          <button onClick={onClose}
            style={{ background: "none", border: "none", fontSize: 18, color: "#9ca3af", cursor: "pointer", lineHeight: 1, padding: "2px 4px", borderRadius: 6 }}>✕</button>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 20px", borderBottom: "1px solid #f3f4f6", background: "#fafafa" }}>
          <button onClick={() => setSelected(new Set(ALL_KEYS))}
            style={{ padding: "5px 12px", fontSize: 12, fontWeight: 600, borderRadius: 6, border: "1px solid #d1d5db", background: "#fff", cursor: "pointer", color: "#374151" }}>✔ Select All</button>
          <button onClick={() => setSelected(new Set())}
            style={{ padding: "5px 12px", fontSize: 12, fontWeight: 600, borderRadius: 6, border: "1px solid #d1d5db", background: "#fff", cursor: "pointer", color: "#374151" }}>✕ Clear All</button>
          <span style={{ marginLeft: "auto", fontSize: 12, color: "#6b7280" }}>{selected.size} field{selected.size !== 1 ? "s" : ""} selected</span>
        </div>
        <div style={{ padding: "8px 0", maxHeight: "50vh", overflowY: "auto" }}>
          {fieldGroups.map((g) => {
            const groupKeys = g.fields.map((f) => f.key);
            const allOn  = groupKeys.every((k) => selected.has(k));
            const someOn = groupKeys.some((k) => selected.has(k));
            return (
              <div key={g.group} style={{ marginBottom: 2 }}>
                <div onClick={() => toggleGroup(g.fields)}
                  style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 20px", cursor: "pointer", background: "#f9fafb", borderTop: "1px solid #f3f4f6" }}>
                  <input type="checkbox" checked={allOn}
                    ref={(el) => { if (el) el.indeterminate = someOn && !allOn; }}
                    onChange={() => toggleGroup(g.fields)} onClick={(e) => e.stopPropagation()} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>{g.group}</span>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4, padding: "8px 20px 10px" }}>
                  {g.fields.map((f) => (
                    <label key={f.key} style={{
                      display: "flex", alignItems: "center", gap: 5, padding: "4px 10px", borderRadius: 6,
                      fontSize: 12, cursor: "pointer",
                      border: `1px solid ${selected.has(f.key) ? "#6366f1" : "#e5e7eb"}`,
                      background: selected.has(f.key) ? "#eef2ff" : "#fff",
                      color: selected.has(f.key) ? "#4338ca" : "#374151",
                      fontWeight: selected.has(f.key) ? 600 : 400, transition: "all 0.1s",
                    }}>
                      <input type="checkbox" checked={selected.has(f.key)} onChange={() => toggle(f.key)} style={{ display: "none" }} />
                      {f.label}
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, padding: "14px 20px", borderTop: "1px solid #f3f4f6", position: "sticky", bottom: 0, background: "#fff" }}>
          <button onClick={onClose}
            style={{ padding: "9px 18px", background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: "pointer" }}>Cancel</button>
          <button onClick={() => onExport([...selected], labelOf)} disabled={selected.size === 0}
            style={{ padding: "9px 20px", background: selected.size === 0 ? "#a5b4fc" : "#6366f1", color: "#fff", border: "none", borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: selected.size === 0 ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: 6 }}>
            ⬇ Export {selected.size > 0 ? `(${selected.size} fields)` : ""}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Pagination Controls ─────────────────────────────────────────────────────
// Builds the page-number array: e.g. [1,2,3,'…',9,10] or [1,'…',4,5,6,'…',10]
function buildPageNumbers(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [];
  // Always show first 2
  pages.push(1);
  if (current > 4) pages.push("…");
  // Window around current
  const start = Math.max(2, current - 1);
  const end   = Math.min(total - 1, current + 1);
  for (let i = start; i <= end; i++) pages.push(i);
  if (current < total - 3) pages.push("…");
  // Always show last 2
  if (total > 1) pages.push(total);
  return pages;
}

function PaginationBar({ currentPage, totalPages, totalCount, hasMore, pageLoading, onPrev, onNext, onGoTo }) {
  const pages = totalPages > 0 ? buildPageNumbers(currentPage, totalPages) : [];

  const navBtn = (disabled) => ({
    display: "flex", alignItems: "center", justifyContent: "center",
    width: 34, height: 34, borderRadius: 8, fontSize: 13,
    fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer",
    border: "1px solid #e5e7eb",
    background: disabled ? "#f9fafb" : "#fff",
    color: disabled ? "#d1d5db" : "#374151",
    transition: "all 0.15s", flexShrink: 0,
  });

  const pageBtn = (active) => ({
    display: "flex", alignItems: "center", justifyContent: "center",
    minWidth: 34, height: 34, borderRadius: 8, fontSize: 13,
    fontWeight: active ? 700 : 500, cursor: active ? "default" : "pointer",
    border: `1px solid ${active ? "#6366f1" : "#e5e7eb"}`,
    background: active ? "#6366f1" : "#fff",
    color: active ? "#fff" : "#374151",
    transition: "all 0.15s",
  });

  return (
    <div style={{
      borderTop: "1px solid #f3f4f6", marginTop: 10, paddingTop: 14,
    }}>
      {/* Total count row */}
      {totalCount > 0 && (
        <div style={{
          textAlign: "center", fontSize: 12, color: "#6b7280",
          marginBottom: 10, fontWeight: 500,
        }}>
          Total{" "}
          <span style={{ fontWeight: 700, color: "#374151" }}>{totalCount}</span>
          {" "}records
          {totalPages > 0 && (
            <> &nbsp;·&nbsp; Page <span style={{ fontWeight: 700, color: "#374151" }}>{currentPage}</span> of <span style={{ fontWeight: 700, color: "#374151" }}>{totalPages}</span></>
          )}
        </div>
      )}

      {/* Pagination row */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4, flexWrap: "wrap" }}>
        {/* Prev arrow */}
        <button
          onClick={onPrev}
          disabled={currentPage === 1 || pageLoading}
          style={navBtn(currentPage === 1 || pageLoading)}
          title="Previous page"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>

        {/* Page numbers */}
        {pageLoading ? (
          <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "0 8px" }}>
            <span style={{
              width: 14, height: 14,
              border: "2px solid #6366f1", borderTopColor: "transparent",
              borderRadius: "50%", display: "inline-block",
              animation: "spin 0.7s linear infinite",
            }} />
            <span style={{ fontSize: 12, color: "#9ca3af" }}>Loading…</span>
          </div>
        ) : pages.length > 0 ? (
          pages.map((p, idx) =>
            p === "…" ? (
              <span key={`ellipsis-${idx}`} style={{ padding: "0 2px", color: "#9ca3af", fontSize: 13, userSelect: "none" }}>…</span>
            ) : (
              <button
                key={p}
                onClick={() => p !== currentPage && onGoTo(p)}
                style={pageBtn(p === currentPage)}
              >
                {p}
              </button>
            )
          )
        ) : (
          // totalPages unknown (still exploring) — show current page pill
          <div style={pageBtn(true)}>{currentPage}</div>
        )}

        {/* Next arrow */}
        <button
          onClick={onNext}
          disabled={(!hasMore && !totalPages) || (totalPages && currentPage === totalPages) || pageLoading}
          style={navBtn((!hasMore && !totalPages) || (totalPages && currentPage === totalPages) || pageLoading)}
          title="Next page"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
      </div>
    </div>
  );
}

// ── Main UserList ───────────────────────────────────────────────────────────
export default function UserList({ user }) {
  const [users, setUsers]               = useState([]);
  const [allUsers, setAllUsers]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [pageLoading, setPageLoading]   = useState(false);
  const [expanded, setExpanded]         = useState(null);
  const [search, setSearch]             = useState("");
  const [showModal, setShowModal]       = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting]         = useState(false);
  const [editTarget, setEditTarget]     = useState(null);
  const [saving, setSaving]             = useState(false);
  const [saveSuccess, setSaveSuccess]   = useState(null);

  // ── Pagination state ──────────────────────────────────────────────────────
  const [currentPage, setCurrentPage]   = useState(1);
  const [hasMore, setHasMore]           = useState(true);
  const [lastDoc, setLastDoc]           = useState(null);
  const [totalCount, setTotalCount]     = useState(0);
  const [totalPages, setTotalPages]     = useState(0);
  // pageCursors[i] = the Firestore doc to startAfter() to reach page i+1
  // pageCursors[0] = null  → page 1 starts from beginning
  const [pageCursors, setPageCursors]   = useState([null]);

  // ── Core fetch (cursor = null means first page) ───────────────────────────
  // fetchPage: fetches one page starting after `cursor`.
  // Returns { lastDoc, hasMore, snapDocs } so callers can chain fetches.
  const fetchPage = useCallback(async (cursor = null, showSpinner = false) => {
    showSpinner ? setLoading(true) : setPageLoading(true);
    try {
      const ref = collection(db, "admins", user.uid, "users");
      const constraints = [orderBy("createdAt", "asc"), limit(PAGE_SIZE)];
      if (cursor) constraints.push(startAfter(cursor));

      const snap = await getDocs(query(ref, ...constraints));
      const docs  = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const ld    = snap.docs[snap.docs.length - 1] ?? null;
      const more  = snap.docs.length === PAGE_SIZE;

      setUsers(docs);
      setLastDoc(ld);
      setHasMore(more);
      return { lastDoc: ld, hasMore: more, snapDocs: snap.docs };
    } catch (err) {
      console.error("fetchPage error:", err);
      return { lastDoc: null, hasMore: false, snapDocs: [] };
    } finally {
      setLoading(false);
      setPageLoading(false);
    }
  }, [user.uid]);

  const fetchAll = useCallback(async () => {
    try {
      const ref  = collection(db, "admins", user.uid, "users");
      const snap = await getDocs(ref);
      setAllUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error(err);
    }
  }, [user.uid]);

  // Fetch total record count for pagination display
  const fetchTotalCount = useCallback(async () => {
    try {
      const ref  = collection(db, "admins", user.uid, "users");
      const snap = await getDocs(ref);
      const count = snap.size;
      setTotalCount(count);
      setTotalPages(Math.ceil(count / PAGE_SIZE));
    } catch (err) {
      console.error("fetchTotalCount error:", err);
    }
  }, [user.uid]);

  // Initial load
  useEffect(() => { fetchPage(null, true); fetchTotalCount(); }, [fetchPage, fetchTotalCount]);

  // Load allUsers when search is active
  useEffect(() => {
    if (search && allUsers.length === 0) fetchAll();
  }, [search, allUsers.length, fetchAll]);

  // ── Pagination handlers ───────────────────────────────────────────────────
  // pageCursors[i] = the Firestore doc to startAfter() to REACH page (i+1).
  // e.g. pageCursors[0]=null (reach page 1 = start), pageCursors[1]=lastDocOfPage1 (reach page 2), etc.

  // Walk from `fromPage` up to `toPage`, collecting cursors along the way.
  // Returns the cursor needed to display `toPage`.
  const walkToCursor = useCallback(async (fromPage, toPage, cursors) => {
    let cur = cursors[fromPage - 1] ?? null; // cursor that gets us to fromPage
    const newCursors = [...cursors];

    for (let p = fromPage; p < toPage; p++) {
      // If we already have the cursor for the NEXT page, skip the fetch
      if (newCursors[p] != null) {
        cur = newCursors[p];
        continue;
      }
      // Fetch page p silently (no spinner, just to get its lastDoc as cursor for p+1)
      const ref = collection(db, "admins", user.uid, "users");
      const constraints = [orderBy("createdAt", "asc"), limit(PAGE_SIZE)];
      if (cur) constraints.push(startAfter(cur));
      const snap = await getDocs(query(ref, ...constraints));
      cur = snap.docs[snap.docs.length - 1] ?? null;
      newCursors[p] = cur; // this cursor reaches page p+1
    }
    return { cursor: cur, newCursors };
  }, [user.uid]);

  const handleNext = async () => {
    if (!hasMore || pageLoading) return;
    const nextPage = currentPage + 1;
    // Store lastDoc as the cursor that reaches nextPage
    const newCursors = [...pageCursors];
    newCursors[currentPage] = lastDoc;
    setPageCursors(newCursors);
    await fetchPage(lastDoc);
    setCurrentPage(nextPage);
    setExpanded(null);
  };

  const handlePrev = async () => {
    if (currentPage === 1 || pageLoading) return;
    const prevPage   = currentPage - 1;
    const prevCursor = pageCursors[prevPage - 1] ?? null;
    await fetchPage(prevCursor);
    setCurrentPage(prevPage);
    setExpanded(null);
  };

  const handleGoTo = async (pageNum) => {
    if (pageLoading || pageNum === currentPage) return;

    // Case 1: We already have the cursor for this page — jump directly
    if (pageCursors[pageNum - 1] !== undefined) {
      const cursor = pageCursors[pageNum - 1] ?? null;
      await fetchPage(cursor);
      setCurrentPage(pageNum);
      setExpanded(null);
      return;
    }

    // Case 2: Need to walk forward from the furthest known cursor
    // Find the highest page whose cursor we know
    let fromPage = 1;
    for (let i = pageCursors.length - 1; i >= 0; i--) {
      if (pageCursors[i] !== undefined) { fromPage = i + 1; break; }
    }

    const { cursor, newCursors } = await walkToCursor(fromPage, pageNum, pageCursors);
    setPageCursors(newCursors);
    await fetchPage(cursor);
    setCurrentPage(pageNum);
    setExpanded(null);
  };

  // Refresh the current page in-place (used after delete)
  const refreshCurrentPage = useCallback(async () => {
    const cursor = pageCursors[currentPage - 1] ?? null;
    await fetchPage(cursor);
  }, [currentPage, pageCursors, fetchPage]);

  // ── Delete handler ────────────────────────────────────────────────────────
  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const ref = doc(db, "admins", user.uid, "users", deleteTarget.id);
      await deleteDoc(ref);
      setAllUsers((prev) => prev.filter((u) => u.id !== deleteTarget.id));
      if (expanded === deleteTarget.id) setExpanded(null);
      setDeleteTarget(null);
      // Refresh the current page so it fills the gap
      await refreshCurrentPage();
    } catch (err) {
      console.error("Delete failed:", err);
      alert("Failed to delete record. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  // ── Edit / Save handler ───────────────────────────────────────────────────
  const handleEditSave = async (updatedData) => {
    if (!editTarget) return;
    setSaving(true);
    try {
      const ref = doc(db, "admins", user.uid, "users", editTarget.id);
      await updateDoc(ref, { ...updatedData, updatedAt: new Date() });

      const merge = (prev) =>
        prev.map((u) => (u.id === editTarget.id ? { ...u, ...updatedData } : u));
      setUsers(merge);
      setAllUsers(merge);

      setSaveSuccess(editTarget.id);
      setEditTarget(null);
      setTimeout(() => setSaveSuccess(null), 3000);
    } catch (err) {
      console.error("Save failed:", err);
      alert("Failed to save changes. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // ── Filtered list (search mode uses allUsers, normal mode uses paged users) ──
  const sourceData    = search ? allUsers : users;
  const filteredUsers = sourceData.filter((u) => {
    const v = search.toLowerCase();
    return (
      u.fullName?.toLowerCase().includes(v)    ||
      u.code?.toLowerCase().includes(v)        ||
      u.dpId?.toLowerCase().includes(v)        ||
      u.pan?.toLowerCase().includes(v)         ||
      u.bank1?.toLowerCase().includes(v)       ||
      u.bank2?.toLowerCase().includes(v)       ||
      u.primaryBank?.toLowerCase().includes(v)
    );
  });

  const maxBanks    = Math.max(1, ...filteredUsers.map((u) => u.bankDetails?.length ?? 0));
  const fieldGroups = buildFieldGroups(maxBanks);
  const ALL_KEYS    = fieldGroups.flatMap((g) => g.fields.map((f) => f.key));

  // ── Export handler ────────────────────────────────────────────────────────
  const handleExport = (selectedKeys, labelOf) => {
    const data = filteredUsers.map((u) => {
      const row = {};
      ALL_KEYS.forEach((key) => {
        if (!selectedKeys.includes(key)) return;
        row[labelOf(key)] = resolveField(u, key);
      });
      return row;
    });

    const ws = XLSX.utils.json_to_sheet(data);
    const headers = Object.keys(data[0] ?? {});
    ws["!cols"] = headers.map((header) => ({
      wch: Math.min(40, Math.max(header.length, ...data.map((r) => String(r[header] ?? "").length)) + 2),
    }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Clients");
    XLSX.writeFile(wb, `clients_export_${new Date().toISOString().slice(0, 10)}.xlsx`);
    setShowModal(false);
  };

  const toggle = (id) => setExpanded((prev) => (prev === id ? null : id));
  const fmt    = (val) => val && val !== "" ? val : <span style={{ color: "#9ca3af" }}>—</span>;

  // ── Loading state ─────────────────────────────────────────────────────────
  if (loading)
    return (
      <div className="ul-empty">
        <div className="ul-spinner" />
        <p>Loading records…</p>
      </div>
    );

  if (users.length === 0 && !search)
    return (
      <div className="ul-empty">
        <div className="ul-empty-icon">📭</div>
        <h3>No records found</h3>
        <p>Add a client using the <strong>Add Data</strong> tab.</p>
      </div>
    );

  return (
    <>
      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <DeleteModal
          record={deleteTarget}
          onClose={() => !deleting && setDeleteTarget(null)}
          onConfirm={handleDeleteConfirm}
          deleting={deleting}
        />
      )}

      {/* Edit Modal */}
      {editTarget && (
        <EditModal
          record={editTarget}
          onClose={() => !saving && setEditTarget(null)}
          onSave={handleEditSave}
          saving={saving}
        />
      )}

      {/* Export Modal */}
      {showModal && (
        <ExportModal
          total={filteredUsers.length}
          fieldGroups={fieldGroups}
          onClose={() => setShowModal(false)}
          onExport={handleExport}
        />
      )}

      <div className="ul-wrap">
        {/* Top bar */}
        <div className="ul-topbar">
          <div>
            <h2 className="ul-title">Client Records</h2>
            <p className="ul-sub">
              {search
                ? <>{filteredUsers.length} record{filteredUsers.length !== 1 ? "s" : ""} found <span style={{ color: "#6366f1", fontWeight: 600 }}>(filtered)</span></>
                : <>{users.length} record{users.length !== 1 ? "s" : ""} on this page · Page {currentPage}</>
              }
            </p>
          </div>
          <button className="ul-export-btn" onClick={() => setShowModal(true)}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Export Excel
            {search && filteredUsers.length > 0 && (
              <span style={{ marginLeft: 6, background: "#6366f1", color: "#fff", borderRadius: 10, fontSize: 11, padding: "1px 6px", fontWeight: 700 }}>
                {filteredUsers.length}
              </span>
            )}
          </button>
        </div>

        {/* Save success toast */}
        {saveSuccess && (
          <div style={{
            margin: "0 0 12px", padding: "10px 14px", background: "#f0fdf4",
            border: "1px solid #bbf7d0", borderRadius: 8, fontSize: 13,
            color: "#166534", display: "flex", alignItems: "center", gap: 8,
          }}>
            ✅ Record updated successfully!
          </div>
        )}

        {/* Search bar */}
        <div className="ul-search-wrap">
          <svg className="ul-search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            className="ul-search"
            placeholder="Search by Name / Unique Code / DP ID / PAN / Bank Name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && <button className="ul-search-clear" onClick={() => setSearch("")}>✕</button>}
        </div>

        {/* Search info banner */}
        {search && (
          <div style={{ margin: "0 0 12px", padding: "8px 14px", background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 8, fontSize: 13, color: "#0369a1", display: "flex", alignItems: "center", gap: 8 }}>
            <span>🔍</span>
            <span>
              Showing <strong>{filteredUsers.length}</strong> result{filteredUsers.length !== 1 ? "s" : ""} for "<strong>{search}</strong>".
              Export will include only these {filteredUsers.length} record{filteredUsers.length !== 1 ? "s" : ""}.
            </span>
          </div>
        )}

        {/* Record list */}
        <div className="ul-list">
          {filteredUsers.length === 0 ? (
            <div className="ul-empty" style={{ padding: "40px 0" }}>
              <div className="ul-empty-icon">🔍</div>
              <h3>No results found</h3>
              <p>Try a different search term.</p>
            </div>
          ) : (
            filteredUsers.map((u, i) => (
              <div className="ul-card" key={u.id} style={{ animationDelay: `${i * 40}ms` }}>
                <div className="ul-card-header" onClick={() => toggle(u.id)}>
                  <div className="ul-avatar">
                    {(u.fullName ? u.fullName[0] : "U").toUpperCase()}
                  </div>
                  <div className="ul-card-info" style={{ minWidth: 0, flex: 1, overflow: "hidden" }}>
                    <span className="ul-card-name" style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.fullName || "—"}</span>
                    <span className="ul-card-meta" style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {u.code || "—"} &nbsp;·&nbsp; {u.dpId || "—"} &nbsp;·&nbsp; {u.pan || "—"}
                    </span>
                  </div>
                  <div className="ul-chip">{u.depository || u.category || "—"}</div>
                  {u.bankDetails?.length > 0 && (
                    <div style={{ fontSize: 11, fontWeight: 700, background: "#eff6ff", color: "#2563eb", borderRadius: 10, padding: "2px 8px", border: "1px solid #bfdbfe", whiteSpace: "nowrap" }}>
                      🏦 {u.bankDetails.length} bank{u.bankDetails.length > 1 ? "s" : ""}
                    </div>
                  )}
                  <EditIconButton onClick={(e) => { e.stopPropagation(); setEditTarget(u); }} />
                  <DeleteIconButton onClick={(e) => { e.stopPropagation(); setDeleteTarget(u); }} />
                  <div className={`ul-chevron ${expanded === u.id ? "open" : ""}`}>▾</div>
                </div>

                {expanded === u.id && (
                  <div className="ul-detail">

                    {/* DP Details */}
                    <div className="ul-detail-section">
                      <div className="ul-detail-heading">🗂️ DP Details</div>
                      <div className="ul-detail-grid">
                        {[
                          ["Full Name",              u.fullName],
                          ["Unique Client Code",     u.code],
                          ["DP Name",                u.dpName],
                          ["Depository",             u.depository],
                          ["Category",               u.category],
                          ["Rep. Assessee",          u.representativeAssessee],
                          ["DP ID + BO Client ID",   u.dpId,        true],
                          ["PAN",                    u.pan,         true],
                          ["DOB / DOI",              u.dob],
                          ["Name as per PAN",        u.nameAsPerPan],
                          ["Mobile (MO)",            u.mobile],
                          ["Email",                  u.email],
                          ["Joint With",             u.jointWith],
                          ["UPI ID",                 u.upiId],
                          ["Bank 1",                 u.bank1],
                          ["Bank 2",                 u.bank2],
                          ["Primary Bank",           u.primaryBank],
                          ["Aadhaar No",             u.aadhaar,     true],
                          ["Note (DP)",              u.dpNote],
                        ].map(([label, val, mono]) => (
                          <div className="ul-detail-field" key={label}>
                            <span>{label}</span>
                            <strong className={mono ? "mono" : ""}>{fmt(val)}</strong>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Nominee */}
                    {u.nomineeDetails &&
                      (u.nomineeDetails.nameAsPerPan || u.nomineeDetails.aadhaarNumber || u.nomineeDetails.relation) && (
                      <div className="ul-detail-section">
                        <div className="ul-detail-heading">👥 Nominee Details</div>
                        <div className="ul-detail-grid">
                          <div className="ul-detail-field">
                            <span>Name as per PAN</span>
                            <strong>{fmt(u.nomineeDetails.nameAsPerPan)}</strong>
                          </div>
                          <div className="ul-detail-field">
                            <span>Aadhaar</span>
                            <strong className="mono">{fmt(u.nomineeDetails.aadhaarNumber)}</strong>
                          </div>
                          <div className="ul-detail-field">
                            <span>Relation</span>
                            <strong>{fmt(u.nomineeDetails.relation)}</strong>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Bank Details */}
                    {u.bankDetails?.length > 0 && (
                      <div className="ul-detail-section">
                        <div className="ul-detail-heading">🏦 Bank Details</div>
                        {u.bankDetails.map((b, bi) => (
                          <div className="ul-bank-block" key={bi}>
                            <div className="ul-bank-badge">Account #{bi + 1}</div>
                            <div className="ul-detail-grid">
                              {[
                                ["Bank Name",        b.bankName],
                                ["Branch",           b.branch],
                                ["Name as per Bank", b.nameAsPerBank],
                                ["Account No",       b.accNumber,    true],
                                ["Account Type",     b.accountType],
                                ["IFSC",             b.ifsc,         true],
                                ["MICR",             b.micr,         true],
                                ["Customer ID",      b.customerId,   true],
                                ["Net Banking ID",   b.netBankingId],
                                ["MO (Bank)",        b.mo],
                              ].map(([label, val, mono]) => (
                                <div className="ul-detail-field" key={label}>
                                  <span>{label}</span>
                                  <strong className={mono ? "mono" : ""}>{fmt(val)}</strong>
                                </div>
                              ))}
                              {b.email && (
                                <div className="ul-detail-field" key="Email (Bank)" style={{ gridColumn: "1 / -1" }}>
                                  <span>Email (Bank)</span>
                                  <strong style={{ wordBreak: "break-all", overflowWrap: "anywhere" }}>{fmt(b.email)}</strong>
                                </div>
                              )}
                              {[
                                ["ATM Card No", b.atmCardNo, true],
                                ["Expiry",      b.expiry,    true],
                                ["CVV",         b.cvv,       true],
                                ["Note",        b.note],
                              ].map(([label, val, mono]) => (
                                <div className="ul-detail-field" key={label}>
                                  <span>{label}</span>
                                  <strong className={mono ? "mono" : ""}>{fmt(val)}</strong>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Other Details */}
                    {(u.reference || u.sourceForIpo || u.note) && (
                      <div className="ul-detail-section">
                        <div className="ul-detail-heading">📝 Other Details</div>
                        <div className="ul-detail-grid">
                          <div className="ul-detail-field">
                            <span>Reference</span><strong>{fmt(u.reference)}</strong>
                          </div>
                          <div className="ul-detail-field">
                            <span>Source for IPO</span><strong>{fmt(u.sourceForIpo)}</strong>
                          </div>
                          <div className="ul-detail-field">
                            <span>Note</span><strong>{fmt(u.note)}</strong>
                          </div>
                        </div>
                      </div>
                    )}

                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* ── Pagination bar (hidden while search is active) ── */}
        {!search && (
          <PaginationBar
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            hasMore={hasMore}
            pageLoading={pageLoading}
            onPrev={handlePrev}
            onNext={handleNext}
            onGoTo={handleGoTo}
          />
        )}
      </div>
    </>
  );
}